'use client';import { type Reservation } from '@parkingbase/ecommerce-api-dev';


import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';

import { Link } from '@/lib/app-router';
import { cn } from '@/lib/styleUtils';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonVariant } from '@/components/ui/button/Button.types';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import type { ActiveSessionsPopupProps, ActiveSessionRowProps } from './ActiveSessionsPopup.types';
import {
  popoverContainerClasses,
  popoverTitleClasses,
  overlayClasses,
  sessionRowClasses,
  rowBgNormal,
  rowBgWarning,
  rowBgError,
  rowInfoClasses,
  rowDotClasses,
  rowDotActiveClasses,
  rowDotUpcomingClasses,
  rowDotExpiredClasses,
  rowContentClasses,
  rowTopLineClasses,
  rowGarageNameClasses,
  rowTimeTextClasses,
  rowTimeActiveClasses,
  rowTimeExpiredClasses,
  rowVehicleTextClasses,
  rowActionsClasses,
} from './ActiveSessionsPopup.style';

const WARN_THRESHOLD_MINUTES = 30;
const ERROR_THRESHOLD_MINUTES = 15;

function getRowBg(session: ActiveSessionRowProps): string {
  // Urgency backgrounds only apply to active sessions, not upcoming
  if (session.sessionState === 'upcoming') return rowBgNormal;
  if (session.isExpired) return rowBgError;
  if (session.minutesRemaining == null) return rowBgNormal;
  if (session.minutesRemaining <= ERROR_THRESHOLD_MINUTES) return rowBgError;
  if (session.minutesRemaining <= WARN_THRESHOLD_MINUTES) return rowBgWarning;
  return rowBgNormal;
}

function getDotClass(session: ActiveSessionRowProps): string {
  if (session.isExpired) return rowDotExpiredClasses;
  if (session.sessionState === 'upcoming') return rowDotUpcomingClasses;
  return rowDotActiveClasses;
}

// Reset native <button> chrome so a clickable row matches the <Link> variant.
const rowInfoButtonReset = 'tw:bg-transparent tw:border-0 tw:text-left tw:cursor-pointer';

function SessionRow({
  session,
  onClose,
  onViewPass,
}: {
  session: ActiveSessionRowProps;
  onClose: () => void;
  onViewPass?: (reservation: Reservation) => void;
}) {
  const t = useTranslations('ActiveSession');
  const bgClass = getRowBg(session);
  const dotClass = getDotClass(session);
  const isPass = session.viewMode === 'pass';

  // 'pass' rows open the View Pass modal (transient parking-history ticket);
  // closing the popup first keeps focus/scroll sane before the modal mounts.
  const handleViewPass = () => {
    onClose();
    if (session.passReservation) onViewPass?.(session.passReservation);
  };

  const info = (
    <>
      <span className={cn(rowDotClasses, dotClass)} aria-hidden="true" />
      <div className={rowContentClasses}>
        <div className={rowTopLineClasses}>
          <span className={rowGarageNameClasses} data-cy="active-session-garage">
            {session.garageName}
          </span>
          {session.timeText && (
            <span
              className={cn(
                rowTimeTextClasses,
                session.isExpired ? rowTimeExpiredClasses : rowTimeActiveClasses
              )}
              data-cy="active-session-time"
            >
              {session.timeText}
            </span>
          )}
        </div>
        {session.ticketNumber && (
          <span
            className={rowVehicleTextClasses}
            aria-label={t('ticketLabel', { number: session.ticketNumber })}
            data-cy="active-session-ticket"
          >
            #{session.ticketNumber}
          </span>
        )}
        {session.vehicleText && (
          <span className={rowVehicleTextClasses} data-cy="active-session-vehicle">
            {session.vehicleText}
          </span>
        )}
      </div>
    </>
  );

  return (
    <div className={cn(sessionRowClasses, bgClass)} data-cy="active-session-row">
      {/* Info section */}
      {isPass ? (
        <button
          type="button"
          className={cn(rowInfoClasses, rowInfoButtonReset)}
          onClick={handleViewPass}
          data-cy="active-session-info"
        >
          {info}
        </button>
      ) : (
        <Link
          href={session.href}
          className={rowInfoClasses}
          onClick={onClose}
          data-cy="active-session-info"
        >
          {info}
        </Link>
      )}

      {/* Action buttons */}
      <div className={rowActionsClasses}>
        {isPass ? (
          <Button
            size={ButtonSize.small}
            variant={ButtonVariant.secondary}
            onClick={handleViewPass}
            data-cy="active-session-view-button"
          >
            {t('view')}
          </Button>
        ) : (
          <Link href={session.href} onClick={onClose}>
            <Button
              size={ButtonSize.small}
              variant={ButtonVariant.secondary}
              data-cy="active-session-view-button"
            >
              {t('view')}
            </Button>
          </Link>
        )}
        {!isPass && session.amountDue && (
          <Link href={session.href} onClick={onClose}>
            <Button
              size={ButtonSize.small}
              variant={ButtonVariant.primary}
              data-cy="active-session-pay-button"
            >
              {t('pay', { amount: session.amountDue })}
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

export function ActiveSessionsPopup({
  open,
  onClose,
  sessions,
  anchorRef,
  onViewPass,
}: ActiveSessionsPopupProps) {
  const t = useTranslations('ActiveSession');
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);

  useFocusTrap(popoverRef, open && isDesktop);

  // Position popover below the anchor
  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      right: Math.max(window.innerWidth - rect.right, 8),
    });
  }, [open, anchorRef]);

  // Escape key to close
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const title = t('activeSessions');

  const sessionList = sessions.map((session) => (
    <SessionRow key={session.id} session={session} onClose={onClose} onViewPass={onViewPass} />
  ));

  if (isDesktop) {
    // Desktop: popover via portal
    if (!position) return null;
    return createPortal(
      <>
        <div className={overlayClasses} onClick={onClose} aria-hidden="true" data-cy="active-sessions-popup-overlay" />
        <div
          ref={popoverRef}
          className={cn(popoverContainerClasses, 'tw:fixed tw:z-50')}
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed popover position)
          style={{ top: position.top, right: position.right }}
          role="menu"
          aria-label={title}
          data-cy="active-sessions-popup"
        >
          <div className={popoverTitleClasses}>{title}</div>
          {sessionList}
        </div>
      </>,
      document.body
    );
  }

  // Mobile: BottomSheet
  return (
    <BottomSheet open={open} onClose={onClose} title={title} ariaLabel={title} data-cy="active-sessions-popup-mobile">
      {sessionList}
    </BottomSheet>
  );
}
